import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  UserCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useUserList, useRoles, useDeleteUser } from "../hooks/useUsers";
import { UserTable } from "../components/UserTable";
import { NewUserDrawer } from "../components/NewUserDrawer";
import { AppLayout } from "@/components/layout/AppLayout";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import type { UserStatus } from "../types";

const USER_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "status", label: "Status" },
  { key: "roles", label: "Roles" },
  { key: "createdAt", label: "Created At" },
];

const PAGE_SIZES = [5, 10, 25, 50];

export default function UserListPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<UserStatus | "">("");
  const [roleId, setRoleId] = useState("");
  const [isNewUserOpen, setIsNewUserOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const filters = useMemo(
    () => ({ page, limit, search, status, roleId }),
    [page, limit, search, status, roleId],
  );

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    minReached,
  } = useColumnVisibility("users", USER_COLUMNS);

  const { data, isLoading, isError, error } = useUserList(filters);
  const { data: roles = [] } = useRoles();
  const deleteUser = useDeleteUser();

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteUser.mutateAsync(deleteTarget.id);
  }

  const users = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "User Management" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserCircle className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
          </div>
          <div className="flex items-center gap-2">
            <ColumnSelectorButton
              columns={USER_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setIsNewUserOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New User
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load users</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as UserStatus | "");
                  setPage(1);
                }}
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </Select>
              <Select
                value={roleId}
                onChange={(e) => {
                  setRoleId(e.target.value);
                  setPage(1);
                }}
                className="w-48"
              >
                <option value="">All Roles</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                  Loading users…
                </div>
              ) : (
                <UserTable
                  users={users}
                  isLoading={isLoading}
                  onDelete={(id, name) => setDeleteTarget({ id, name })}
                />
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-6 py-3 text-xs text-muted-foreground"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                <div>
                  Showing {start}-{end} of {total} entries
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span>Show</span>
                    <Select
                      value={String(limit)}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      className="w-20"
                    >
                      {PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Select>
                    <span>entries</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <span className="px-2 text-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewUserDrawer
        isOpen={isNewUserOpen}
        onClose={() => setIsNewUserOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete user?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? They will lose access to PhazeX Hub.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
